import { getColorPalette } from '../lib/colors'
import { Shard } from '../lib/types'

/**
 * Assign different colors to blocks in a shard with no adjacent blocks having the same color
 */
export function assignColorsToShardBlocks(shard: Shard): void {
  const blocks = shard.blocks
  // Define our color palette
  const colors = getColorPalette().sort(() => Math.random() - 0.5)

  // Create an adjacency map for the blocks
  const adjacencyMap = new Map<number, number[]>()

  // For each block, find its adjacent blocks
  for (let i = 0; i < blocks.length; i++) {
    const block = blocks[i]!
    adjacencyMap.set(i, [])

    for (let j = 0; j < blocks.length; j++) {
      if (i === j) continue

      const otherBlock = blocks[j]!
      // Check if blocks are adjacent (horizontally, vertically, or diagonally)
      const dx = Math.abs(block.x - otherBlock.x)
      const dy = Math.abs(block.y - otherBlock.y)

      if (dx <= 1 && dy <= 1) {
        adjacencyMap.get(i)!.push(j)
      }
    }
  }

  // Assign colors using a greedy algorithm that cycles through all colors
  const blockColors: { r: number; g: number; b: number }[] = new Array(blocks.length)

  // Keep track of color usage count to ensure we use all colors evenly
  const colorUsageCount = new Array(colors.length).fill(0)

  // Start from a random color index
  const initialColorIndex = Math.floor(Math.random() * colors.length)

  // Process blocks in order
  for (let i = 0; i < blocks.length; i++) {
    // Get colors used by adjacent blocks
    const usedColorIndices = new Set<number>()
    for (const adjIndex of adjacencyMap.get(i) || []) {
      if (blockColors[adjIndex]) {
        // Find the index of this color in our palette
        const colorIndex = colors.findIndex(
          (c) =>
            c.r === blockColors[adjIndex]!.r &&
            c.g === blockColors[adjIndex]!.g &&
            c.b === blockColors[adjIndex]!.b,
        )
        if (colorIndex !== -1) {
          usedColorIndices.add(colorIndex)
        }
      }
    }

    // Find the least used color that isn't used by adjacent blocks
    let leastUsedCount = Number.POSITIVE_INFINITY
    let selectedColorIndex = -1

    // Start checking from random initial index
    for (let j = 0; j < colors.length; j++) {
      const colorIndex = (initialColorIndex + j) % colors.length
      if (!usedColorIndices.has(colorIndex) && colorUsageCount[colorIndex] < leastUsedCount) {
        leastUsedCount = colorUsageCount[colorIndex]
        selectedColorIndex = colorIndex
      }
    }

    // If all colors are used by adjacent blocks, find the least used color
    if (selectedColorIndex === -1) {
      for (let j = 0; j < colors.length; j++) {
        if (colorUsageCount[j] < leastUsedCount) {
          leastUsedCount = colorUsageCount[j]
          selectedColorIndex = j
        }
      }
    }

    // Assign the color and increment its usage count
    blocks[i]!.color = colors[selectedColorIndex]!
    colorUsageCount[selectedColorIndex]++
  }
}
